import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, Text, DropdownButton, Alert, Tabs, SearchBar } from '@artprov/design-system';
import { useQuery } from "@tanstack/react-query";
import { getMatchingJudicial } from './data/matchingJudicial.data';
import { getEstadisticas, parseEstadisticas } from './data/estadisticas.data';
import { AccionesCell } from './components/AccionesCell';
import { PorcentajeMatchCell } from './components/PorcentajeMatchCell';
import { GrupoCell } from './components/GrupoCell';
import { MatchingJudicialTable } from './components/MatchingJudicialTable';
import ModalFilter from './components/ModalFilter';
import TagsFilters from './components/TagsFilters';
import { formatCurrency } from '../../common/utils/functions/formatCurrency';
import { useMatchingJudicialFiltersStore } from './zustand/filters.store';
import { errorService } from '../../common/utils/functions/error.service';
import { useDebounce } from '../../hooks/useDebounce';
import { useMatchingJudicialUI } from './hooks/useMatchingJudicialUI';
import { useMatchingJudicialActions } from './hooks/useMatchingJudicialActions';
import { IndicadoresGrid } from '../../common/components/IndicadoresGrid';
import { getFechaInicialDesde } from '../../common/utils/functions/dateUtils';
import ModalAsignar from './components/ModalAsignar';
import ModalResultadoJudicial from './components/ModalResultadoJudicial';
import ModalAccionMasiva from '../../common/components/ModalAccionMasiva';
import { indicadoresJudicialConfig } from './config/indicadores.config';

const defaultStats = Object.freeze({
    sinAsignar: 0,
    montoSinAsignar: formatCurrency(0),
    asignadosHoy: 0,
    match100: 0,
    matchMenor100: 0,
    asignados: 0
});

const emptyState = Object.freeze({
    icon: "error",
    title: "No hay registros para mostrar en este momento",
    description: "Intentá cambiar los filtros o actualizar más tarde.",
    iconStyles: "text-warning-20",
    titleStyles: "text-primary-10"
});

const MIN_SEARCH_LENGTH = 3;

const getItemFlag = (item) => {
    if (item.CARGA_MANUAL === 'S') {
        return {
            description: "Este embargo fue cargado manualmente, para gestionarlo ingresá al aplicativo de Legales.",
            icon: "lab_profile",
            color: "default",
        };
    }
    if (item.ERROR_LEGALES) {
        return {
            description: item.ERROR_LEGALES,
            icon: "info",
            color: "error",
        };
    }
    return null;
};

const sanitizeFilters = (filters = {}) => {
    const sanitized = {};

    if (filters.solapa) {
        sanitized.solapa = filters.solapa;
    }

    if (filters.search && filters.search.trim().length >= MIN_SEARCH_LENGTH) {
        sanitized.search = filters.search.trim();
    }

    if (filters.fechaAltaDesde) {
        sanitized.fechaAltaDesde = filters.fechaAltaDesde;
    }

    if (filters.fechaAltaHasta) {
        sanitized.fechaAltaHasta = filters.fechaAltaHasta;
    }

    if (filters.fechaDebitoDesde) {
        sanitized.fechaDebitoDesde = filters.fechaDebitoDesde;
    }

    if (filters.fechaDebitoHasta) {
        sanitized.fechaDebitoHasta = filters.fechaDebitoHasta;
    }

    if (filters.coincidenciaDesde !== null && filters.coincidenciaDesde !== undefined) {
        sanitized.coincidenciaDesde = filters.coincidenciaDesde;
    }

    if (filters.coincidenciaHasta !== null && filters.coincidenciaHasta !== undefined) {
        sanitized.coincidenciaHasta = filters.coincidenciaHasta;
    }

    if (filters.jurisdiccion) {
        sanitized.jurisdiccion = filters.jurisdiccion;
    }

    return sanitized;
};

export default function MatchingJudicial() {
    const [tabSelected, setTabSelected] = useState(() => {
        const storedTab = sessionStorage.getItem('tabSelectedMatchingJudicial');
        return storedTab ? JSON.parse(storedTab) : { text: "Pendientes", id: "pendientes" };
    });

    // Custom hooks para UI y acciones
    const ui = useMatchingJudicialUI();
    const actions = useMatchingJudicialActions(ui);

    // Zustand store
    const filters = useMatchingJudicialFiltersStore((state) => state.filters);
    const setFilters = useMatchingJudicialFiltersStore((state) => state.setFilters);
    const setFilter = useMatchingJudicialFiltersStore((state) => state.setFilter);
    const clearFilter = useMatchingJudicialFiltersStore((state) => state.clearFilter);
    const pagination = useMatchingJudicialFiltersStore((state) => state.pagination);
    const sortConfig = useMatchingJudicialFiltersStore((state) => state.sortConfig);
    const initialized = useMatchingJudicialFiltersStore((state) => state.initialized);
    const initialize = useMatchingJudicialFiltersStore((state) => state.initialize);
    const setPagination = useMatchingJudicialFiltersStore((state) => state.setPagination);
    const setSortConfig = useMatchingJudicialFiltersStore((state) => state.setSortConfig);

    const debouncedSearch = useDebounce(filters.search, 300);

    // Inicializar el store
    useEffect(() => {
        if (!initialized) {
            initialize({ filters: { fechaAltaDesde: getFechaInicialDesde() } });
        }
    }, [initialized, initialize]);

    // Sanitizar filtros
    const sanitizedFilters = useMemo(
        () =>
            sanitizeFilters({
                solapa: filters.solapa,
                search: debouncedSearch,
                fechaAltaDesde: filters.fechaAltaDesde,
                fechaAltaHasta: filters.fechaAltaHasta,
                fechaDebitoDesde: filters.fechaDebitoDesde,
                fechaDebitoHasta: filters.fechaDebitoHasta,
                coincidenciaDesde: filters.coincidenciaDesde,
                coincidenciaHasta: filters.coincidenciaHasta,
                jurisdiccion: filters.jurisdiccion,
            }),
        [filters.solapa, debouncedSearch, filters.fechaAltaDesde, filters.fechaAltaHasta, filters.fechaDebitoDesde, filters.fechaDebitoHasta, filters.coincidenciaDesde, filters.coincidenciaHasta, filters.jurisdiccion]
    );

    const filtersKey = useMemo(
        () => JSON.stringify(sanitizedFilters),
        [sanitizedFilters]
    );

    useEffect(() => {
        sessionStorage.setItem('tabSelectedMatchingJudicial', JSON.stringify(tabSelected));

        setFilters({
            solapa: tabSelected.id
        });
    }, [tabSelected, setFilters]);

    useEffect(() => {
        ui.resetSelection();
    }, [filters, ui.resetSelection]);

    const page = pagination.page;
    const pageSize = pagination.pageSize;
    const offset = Math.max(0, (page - 1) * pageSize);

    // Query para obtener registros de matching judicial
    const {
        data: matchingData,
        isLoading: matchingIsLoading,
        isFetching: matchingIsFetching,
        isError: matchingIsError,
        error: matchingErrorData,
    } = useQuery({
        queryKey: ["matching-judicial", filtersKey, page, pageSize],
        enabled: initialized,
        keepPreviousData: true,
        queryFn: ({ signal }) =>
            getMatchingJudicial(
                {
                    ...sanitizedFilters,
                    offset,
                    limit: pageSize,
                },
                signal
            ),
        onError: (err) =>
            errorService.handleError(err, {
                userMessage: "No pudimos obtener los registros de matching judicial.",
                origen: "MatchingJudicial.matchingQuery",
            }),
    });

    // Query para obtener estadísticas
    // Nota: A diferencia de matching bancario, las estadísticas judiciales
    // no responden a cambios de filtros. Se refrescan manualmente tras acciones.
    const { data: estadisticasData } = useQuery({
        queryKey: ["matching-judicial-estadisticas"],
        enabled: initialized,
        keepPreviousData: true,
        queryFn: ({ signal }) => getEstadisticas(signal),
        select: (data) => parseEstadisticas(data) || defaultStats,
        onError: (err) =>
            errorService.handleError(err, {
                userMessage: "No pudimos obtener las estadísticas.",
                origen: "MatchingJudicial.estadisticasQuery",
            }),
    });

    const headers = useMemo(() => {
        const baseHeaders = matchingData?.headers || {};
        return {
            ...baseHeaders,
            "x-current-page": String(page),
            "x-elements-per-page": String(pageSize),
        };
    }, [matchingData, page, pageSize]);

    const estadisticas = estadisticasData || defaultStats;

    const matchingLoading = matchingIsLoading || matchingIsFetching;

    const getMatchingError = () => {
        if (!matchingIsError) {
            return null;
        }
        if (matchingErrorData instanceof Error) {
            return matchingErrorData.message;
        }
        return "No pudimos obtener los registros.";
    };

    const matchingError = getMatchingError();

    // Handler para cambio de página
    const handlePageChange = useCallback(
        ({ page: nextPage, pageSize: nextPageSize, control }) => {
            if (control === "size" && (nextPageSize ?? pageSize) === pageSize) {
                return;
            }

            const resolvedPage = nextPage ?? page;
            const resolvedPageSize = nextPageSize ?? pageSize;

            if (resolvedPage === page && resolvedPageSize === pageSize) {
                return;
            }

            setPagination({ page: resolvedPage, pageSize: resolvedPageSize });
        },
        [page, pageSize, setPagination]
    );

    const handleSearchChange = useCallback(
        (value) => {
            if (value === "") {
                if (filters.search === "") return;
                clearFilter("search");
                return;
            }
            if (filters.search === value) return;
            setFilter("search", value);
        },
        [clearFilter, filters.search, setFilter]
    );

    // Transformar datos para incluir formato de moneda, componentes de grupo, porcentaje y acciones
    const transformedData = useMemo(() => {
        const data = matchingData?.registros || [];
        return data.map((item) => ({
            ...item,
            acciones: <AccionesCell idEmail={item.ID_EMAIL} idJuicio={item.ID_JUICIO} />,
            IMPORTE: formatCurrency(item.IMPORTE),
            PORC_MATCH_VALUE: item.PORC_MATCH,
            PORC_MATCH: <PorcentajeMatchCell value={item.PORC_MATCH} />,
            GRUPO: <GrupoCell value={item.GRUPO} showFullText />,
            flag: getItemFlag(item),
        }));
    }, [matchingData?.registros]);

    const hasErrors = useMemo(() => {
        const data = matchingData?.registros || [];
        return data.some((item) => item.ERROR_LEGALES);
    }, [matchingData?.registros]);

    const hasManualLoads = useMemo(() => {
        const data = matchingData?.registros || [];
        return data.some((item) => item.CARGA_MANUAL === 'S');
    }, [matchingData?.registros]);

    // Handlers de acciones masivas
    const handleAsignar = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('confirm');
        }
    }, [ui]);

    const handleDescartar = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('descartar');
        }
    }, [ui]);

    const handleReprocesar = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('reprocesar');
        }
    }, [ui]);

    if (matchingError) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-4">
                <Icon size="extra-large" variant="error" color="error" />
                <h2 className="typography-h2 text-error-50">Error al cargar los datos</h2>
                <p className="typography-b1 text-tertiary-60">{matchingError}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-primary-50 text-white rounded hover:bg-primary-60"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-5 relative">
            {/* Alerta posicionada absolutamente a la derecha */}
            {ui.alert.show && (
                <div className="absolute top-3 right-4 z-50">
                    <Alert
                        variant={ui.alert.variant}
                        title={ui.alert.title}
                        description={ui.alert.description}
                        onClose={ui.hideAlert}
                        autoDismiss={true}
                        containerStyle={ui.alert.containerStyle || ''}
                        descriptionStyle={ui.alert.descriptionStyle || ''}
                        titleStyle={ui.alert.titleStyle || ''}
                    />
                </div>
            )}

            <div className='mb-5'>
                <Text variant="h6" className="text-primary-20">
                    Sistema de Gestión de Embargos
                </Text>
                <Text variant="body2">
                    Acá podés consultar y gestionar los embargos ingresados
                </Text>
            </div>

            {/* Cards de Estadísticas */}
            <IndicadoresGrid config={indicadoresJudicialConfig} estadisticas={estadisticas} className="mb-5" />

            <div className="w-full flex justify-between border-b border-primary-10 mb-5">
                <div className="flex">
                    <Tabs
                        className="w-[284px]"
                        classNameTabButton="h-[32px]"
                        underline={false}
                        items={[
                            { text: "Pendientes", id: "pendientes" },
                            { text: "Historial", id: "historial" }
                        ]}
                        selected={tabSelected}
                        setSelected={setTabSelected}
                    />
                </div>
            </div>

            {/* Filtros y Tags */}
            <div className='flex items-center gap-4 mb-5'>
                <SearchBar
                    placeholder="Buscar por expediente, CUIJ, carátula o importe"
                    searchValue={filters.search}
                    setSearchValue={handleSearchChange}
                    classNameContainer="min-w-[360px] w-[380px]"
                    opciones={[]}
                />
                <ModalFilter />
                <TagsFilters />

                {tabSelected.id !== "historial" && (
                    <div className='ml-auto'>
                        <DropdownButton
                            textButton="Acciones masivas"
                            menuOptions={[
                                { icon: '', opcion: "Asignar", onClick: handleAsignar },
                                { icon: '', opcion: "Descartar", onClick: handleDescartar },
                                { icon: '', opcion: "Reprocesar", onClick: handleReprocesar },
                            ]}
                            className={{
                                dropdown: '!h-auto',
                                opcionButton: 'text-tertiary-30',
                                listOpcionButton: 'z-50',
                            }}
                            disabled={ui.selectedRows.length === 0}
                        />
                    </div>
                )}
            </div>

            {/* Tabla con Paginación */}
            <MatchingJudicialTable
                data={transformedData}
                isLoading={matchingLoading}
                onPageChange={handlePageChange}
                headers={headers}
                page={page}
                pageSize={pageSize}
                setSelected={ui.setSelectedRows}
                selected={ui.selectedRows}
                emptyState={emptyState}
                hasErrors={hasErrors}
                hasManualLoads={hasManualLoads}
                selectable={tabSelected.id !== "historial"}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
            />

            {/* Modal de Asignación */}
            {ui.modals.confirm && (
                <ModalAsignar
                    open={ui.modals.confirm}
                    setOpen={(open) => open ? ui.openModal('confirm') : ui.closeModal('confirm')}
                    selectedRows={ui.selectedRows}
                    onConfirm={actions.handleAsignarAccept}
                />
            )}

            {/* Modal de Resultados */}
            {ui.modals.result && (
                <ModalResultadoJudicial
                    open={ui.modals.result}
                    setOpen={(open) => open ? ui.openModal('result') : ui.closeModal('result')}
                    resultData={ui.resultData}
                    onClose={actions.handleCloseResultModal}
                />
            )}

            {/* Modal de Confirmación de Descarte */}
            {ui.modals.descartar && (
                <ModalAccionMasiva
                    open={ui.modals.descartar}
                    setOpen={(open) => open ? ui.openModal('descartar') : ui.closeModal('descartar')}
                    selectedRows={ui.selectedRows}
                    icon="error"
                    iconColor="text-warning-20"
                    iconSize="extra-large"
                    title={"Descartar embargos"}
                    message={"Si descartás la selección, cada registro seleccionado pasará al historial. Los embargos seguirán visibles como “Sin asignar” en Administración de Embargos."}
                    cancelText="Volver"
                    confirmText="Descartar"
                    onConfirm={actions.handleDescartarAccept}
                    width="408px"
                    height="auto"
                />
            )}

            {/* Modal de Confirmación de Reprocesar */}
            {ui.modals.reprocesar && (
                <ModalAccionMasiva
                    open={ui.modals.reprocesar}
                    setOpen={(open) => open ? ui.openModal('reprocesar') : ui.closeModal('reprocesar')}
                    selectedRows={ui.selectedRows}
                    icon="error"
                    iconColor="text-warning-20"
                    iconSize="extra-large"
                    title={"Reprocesar embargos"}
                    message={"Si reprocesás los registros seleccionados, el sistema volverá a buscar coincidencias para estos embargos."}
                    cancelText="Volver"
                    confirmText="Reprocesar"
                    onConfirm={actions.handleReprocesarAccept}
                    width="408px"
                    height="auto"
                />
            )}


            {/* Spinner de Procesamiento */}
            {ui.isProcessing && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-50">
                    <Icon variant="loading" classNameIcon="size-16 text-[64px]" />
                </div>
            )}
        </div>
    );
}
